import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ShippingConfig } from '../_models/shipping-config.interface';

/** Kategorien des Artikelbedarfs. BEDARF = Auftragsbedarf: Material, das der
 *  Techniker vor Ort verbraucht — daraus entsteht KEIN Versand. */
export type RequirementActionType = 'STAGING' | 'VERSAND' | 'BEDARF';

export interface OperationRequirement {
  id: number;
  operation_id: number;
  article_template_id?: number;
  article_template_description?: string;
  free_text?: string;
  quantity: number;
  action_type: RequirementActionType;
  /** Bereits ueber archivierte Lieferscheine versandte Menge (nur fuer
   * VERSAND relevant). Quantity darf nicht darunter fallen. */
  already_shipped_quantity?: number;
  /**
   * Von der Bestandsautomatik erzeugt und nächtlich überschrieben. Eine von
   * Hand bearbeitete Position verliert das Kennzeichen automatisch — sonst
   * wäre die Änderung am nächsten Morgen wieder weg.
   */
  auto_generated?: boolean;
}

/** Verfuegbarkeits-Status einer Auftragsbedarfs-Zeile zum Auftragstermin. */
export type AvailabilityStatus = 'covered' | 'late' | 'missing' | 'unknown';

/** Prognose je Bedarfszeile: was ist zum Auftragstermin voraussichtlich da? */
export interface AvailabilityEntry {
  operation_requirement_id: number;
  article_template_id?: number | null;
  label: string;
  required: number;
  available?: number;
  /** Menge, die zwar unterwegs/geplant ist, aber erst NACH dem Termin ankommt. */
  incoming_late?: number;
  missing?: number;
  status: AvailabilityStatus;
  /** Warum nicht pruefbar: 'freitext' | 'kein_techniker' | 'kein_termin'. */
  unknown_reason?: string;
  detail?: {
    stock_now: number;
    in_transit: number;
    reserved_before: number;
    /** Kontoauszug für die Berechnungstabelle: chronologisch, mit laufendem Saldo. */
    ledger?: LedgerEvent[];
  };
}

/**
 * Ein Ereignis im Kontoauszug einer Artikelvorlage. Zugaenge ('arrival') bringen
 * Material, Vorbelegungen ('need') ziehen es fuer frühere Auftraege ab.
 * `balance` ist der Bestand NACH diesem Ereignis.
 */
export interface LedgerEvent {
  kind: 'arrival' | 'need';
  quantity: number;
  /** Vorzeichenbehaftet: Zugaenge positiv, Vorbelegungen negativ. */
  change: number;
  balance: number;
  date?: string | null;
  /** Liegt nach dem Auftragstermin — erklaert "kommt zu spät", zaehlt nicht mit. */
  after_target: boolean;
  own_operation: boolean;
  ticket_id?: number | null;
  operation_id?: number | null;

  // --- nur bei Zugängen ---
  /** 'transit' = unterwegs, 'planned' = noch nicht versandt. */
  source?: string;
  /** 'versand' | 'staging' — welcher Kanal das Material bringt. */
  channel?: string;
  /** Paket gilt als angekommen, ist aber noch nicht eingebucht. */
  arrived?: boolean;
  /** Woher der Termin stammt: pickup_done | pickup_open | tracking | estimate. */
  arrival_source?: string;
  delivery_note_id?: number | null;
  delivery_note_number?: string | null;
  shipped_date?: string | null;
  pickup_operation_id?: number | null;
  pickup_done?: boolean | null;
}

export interface AvailabilityResult {
  technician: { id: number; name: string } | null;
  operation_date?: string | null;
  computed_at?: string;
  /** Ticket mit Versand-Prioritaet: hier wird kein Material vor Ort verbraucht. */
  material_shipping_order?: boolean;
  entries: AvailabilityEntry[];
}

/** Ein einzelner Artikel-Bedarf einer Operation (kompakte Planungs-Variante:
 *  Backend OperationRequirement#planning_overview_hash). */
export interface ShippingPlanningRequirement {
  id: number;
  label: string;
  quantity: number;
  action_type: 'VERSAND' | 'STAGING';
}

/** Eine Zeile des Bulk-Uebersichts-Endpoints operations/bulk_shipping_planning:
 *  summierte Artikelmengen plus die Einzelartikel je Operation. */
export interface ShippingPlanningOverviewEntry {
  operation_id: number;
  versand_quantity: number;
  staging_quantity: number;
  requirements: ShippingPlanningRequirement[];
}

/** Mini-Referenz eines Lieferscheins für Links in der Versandmatrix. */
export interface DeliveryNoteRef {
  id: number;
  number?: string | null;
}

/** Staging-Versand-Status eines Einbau-Auftrags: Einträge je Anforderung plus
 * die Lieferscheine des Standard-Stagings (Records ohne Config-Zuordnung). */
export interface StagingSummary {
  per_requirement: StagingSummaryEntry[];
  standard_delivery_notes: DeliveryNoteRef[];
}

/** Versand-Status einer STAGING-Anforderung: bereits versandte (diesem
 * Einbau-Auftrag zugeordnete) Staging-Einheiten je Anforderung. */
export interface StagingSummaryEntry {
  operation_requirement_id: number;
  article_template_id?: number;
  article_template_description?: string;
  target: number;
  shipped: number;
  /** Zurueckgehalten (verzoegerter Versand): vorbereitet, aber noch nicht
   * physisch versandt (ship_ready_at gesetzt, shipped_at nil). */
  delayed: number;
  open: number;
}

// ----- Excel-Bulk-Edit Interfaces -------------------------------------------

export type ExcelTemplateSource = 'project' | 'customer';

export interface ExcelChange {
  operation_id: number;
  kind: 'create' | 'update' | 'delete';
  template_id?: number | null;
  free_text?: string | null;
  old_quantity: number;
  new_quantity: number;
  action_type: RequirementActionType;
  error?: string | null;
}

export interface ExcelPreviewColumn {
  id: string;                    // 't:<template_id>' oder 'f:<text>'
  kind: 'template' | 'free_text';
  template_id?: number;
  free_text?: string;
  label: string;                 // Anzeigename in der Diff-Tabelle
  is_staging: boolean;           // wenn Template als Staging-Vorlage im Projekt
}

export interface ExcelPreviewCell {
  old_quantity: number;
  new_quantity: number;
  delta: number;
  has_change: boolean;
  /** True wenn der Wert direkt aus dem Description-Parsing uebernommen wurde
   * (existing == nil und new_quantity == parsed_quantity). Frontend
   * kennzeichnet diese Cells mit blauem Parsing-Hinweis statt grünem +X. */
  is_parsing_takeover?: boolean;
  has_error: boolean;
  error?: string | null;
  action_type: RequirementActionType;
}

export interface ExcelPreviewRow {
  operation_id: number;
  ticket_id?: number;
  order_nr?: string;
  external_order_nr?: string;
  operation_date?: string;
  operation_date_iso?: string;
  store_name?: string;
  technician_name?: string;
  cells: Record<string, ExcelPreviewCell>;
  /** Auftragsbedarf des Auftrags (zweite Excel-Zeile). Leer bei Dateien ohne
   *  Kategorie-Spalte (Export vor der Erweiterung). */
  need_cells?: Record<string, ExcelPreviewCell>;
  /** Mapping operation_requirement_id → Spalten-Token (col_id) + action_type.
   *  Das Inline-Split-Grid mappt damit requirement_split/requirement_committed
   *  der Configs (nach operation_requirement_id gekeyt) auf die Artikel-Spalten. */
  requirements?: Array<{ id: number; action_type: RequirementActionType; col_id: string }>;
  has_change: boolean;
  has_error: boolean;
  /** Plural (Multi-Config): alle noch nicht vollstaendig abgeschlossenen Configs
   *  der Operation, je mit `requirement_split`. Quelle fuer die Pro-Config-
   *  Zeilen in der Versandkonfig-Spalte. */
  shipping_configs?: ShippingConfig[];
  /** Vollstaendig abgeschlossene Configs (beide Kanaele versandt) — read-only
   *  Zeilen im Inline-Split-Grid. Ihre `requirement_committed`-Mengen werden
   *  angezeigt und aus dem offenen Pool herausgerechnet. */
  completed_shipping_configs?: ShippingConfig[];
  /** Form-Meta — wird der ShippingConfigFormComponent als Inputs uebergeben,
   * damit kein separater API-Call pro Cell-Click noetig ist. */
  ticket_store_id?: number;
  ticket_lat?: number;
  ticket_lng?: number;
  project_id?: number;
  operation_technician_id?: number;
  /** Klassifizierungs-Felder fuer den Auto-Create-Workflow. is_internal kommt
   * vom [INTERN]-Praefix im Projektnamen; priority_material_shipping spiegelt
   * das Flag auf der Ticket-Priority. Has-Staging-Artikel wird im Frontend
   * aus den cells abgeleitet (action_type === 'STAGING' && new_quantity > 0). */
  is_internal?: boolean;
  priority_material_shipping?: boolean;
}

export interface ExcelPreviewResult {
  success: boolean;
  errors: string[];
  row_errors: { row: number; error: string }[];
  operation_ids: number[];
  changes: ExcelChange[];
  preview_columns: ExcelPreviewColumn[];
  preview_rows: ExcelPreviewRow[];
}

export interface ExcelApplyResult {
  success: boolean;
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
  /** Atomarer „Zeile pro Config"-Pfad: separat gezaehlte Config-Anlagen/-Updates
   *  (created/updated zaehlen nur Requirements). config_ids mappt Temp-Refs auf
   *  die frisch vergebenen IDs. */
  config_created?: number;
  config_updated?: number;
  config_ids?: Record<string, number>;
}

/** Eine Config-Spec fuer den atomaren Apply: neue Config (mit `ref`) oder
 *  Update einer bestehenden (`config_id`). Restliche Felder = Form-Daten. */
export interface ExcelApplyConfigSpec {
  ref?: string;
  operation_id: number;
  config_id?: number;
  [key: string]: unknown;
}

/** Eine Split-Gruppe je Config (Replace-Semantik; leere requirements leeren). */
export interface ExcelApplySplitGroup {
  operation_id: number;
  config_ref: string;
  requirements: Array<{ action_type: RequirementActionType; template_id?: number; free_text?: string; quantity: number }>;
}

@Injectable({
  providedIn: 'root',
})
export class OperationRequirementService {
  private readonly basePath = environment.apiURL;
  private readonly excelBasePath = environment.apiURL + 'operation_requirements/excel';

  constructor(private http: HttpClient) {}

  async loadRequirements(operationId: number): Promise<OperationRequirement[]> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ requirements: OperationRequirement[] }>(
          this.basePath + `operations/${operationId}/requirements.json`,
          { withCredentials: true }
        )
      );
      return result.requirements;
    } catch (err) {
      console.error('Fehler beim Laden der Requirements:', err);
      return [];
    }
  }

  /** Kompakte Bedarfs-Zeile des Bulk-Endpoints (Backend
   *  OperationRequirement#planning_overview_hash): Label + Menge je Artikel,
   *  ohne already_shipped_quantity (vermeidet N+1 im Backend). */
  async loadShippingPlanningOverview(operationIds: number[]): Promise<ShippingPlanningOverviewEntry[]> {
    if (!operationIds.length) return [];
    let params = new HttpParams();
    operationIds.forEach(id => { params = params.append('operation_ids[]', id.toString()); });
    try {
      const result = await firstValueFrom(
        this.http.get<{ results: ShippingPlanningOverviewEntry[] }>(
          this.basePath + 'operations/bulk_shipping_planning',
          { params, withCredentials: true }
        )
      );
      return result.results;
    } catch (err) {
      console.error('Fehler beim Laden der Artikelbedarf-Übersicht:', err);
      return [];
    }
  }

  /** Lädt den Staging-Versand-Status (Soll/Versandt/Offen je STAGING-Anforderung
   *  plus Standard-Staging-Lieferscheine) für einen Einbau-Auftrag. */
  async loadStagingSummary(operationId: number): Promise<StagingSummary> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ staging: StagingSummary }>(
          this.basePath + `operations/${operationId}/shipping_summary.json`,
          { withCredentials: true }
        )
      );
      return {
        per_requirement: result.staging?.per_requirement ?? [],
        standard_delivery_notes: result.staging?.standard_delivery_notes ?? [],
      };
    } catch (err) {
      console.error('Fehler beim Laden der Staging-Versand-Übersicht:', err);
      return { per_requirement: [], standard_delivery_notes: [] };
    }
  }

  /**
   * Legt einen Artikelbedarf an. `shipping_config_id` bindet ihn optional an ein
   * bestimmtes Versandziel — eine Config-ID oder 'none' für „bewusst keinem
   * Ziel zuordnen" (Staging-Standardversand). Ohne den Wert sucht sich das
   * Backend das Ziel wie bisher selbst.
   *
   * Gibt im Fehlerfall die Servermeldung zurück, statt sie zu verschlucken:
   * der Aufrufer zeigt sie im Dialog an.
   */
  async createRequirement(
    operationId: number,
    data: Partial<OperationRequirement>,
    shippingConfigId?: number | 'none' | null,
  ): Promise<{ requirement: OperationRequirement } | { error: string }> {
    const body: Record<string, unknown> = { ...data };
    if (shippingConfigId != null) body['shipping_config_id'] = shippingConfigId;

    try {
      const result = await firstValueFrom(
        this.http.post<{ requirement: OperationRequirement }>(
          this.basePath + `operations/${operationId}/requirements.json`,
          body,
          { withCredentials: true }
        )
      );
      return { requirement: result.requirement };
    } catch (err) {
      console.error('Fehler beim Erstellen des Requirements:', err);
      const serverError = (err as { error?: { error?: string } } | undefined)?.error?.error;
      return { error: serverError || 'Artikel konnte nicht angelegt werden.' };
    }
  }

  async updateRequirement(id: number, data: Partial<OperationRequirement>): Promise<OperationRequirement | null> {
    try {
      const result = await firstValueFrom(
        this.http.patch<{ requirement: OperationRequirement }>(
          this.basePath + `operation_requirements/${id}.json`,
          data,
          { withCredentials: true }
        )
      );
      return result.requirement;
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Requirements:', err);
      return null;
    }
  }

  async deleteRequirement(id: number): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete(
          this.basePath + `operation_requirements/${id}.json`,
          { withCredentials: true }
        )
      );
      return true;
    } catch (err) {
      console.error('Fehler beim Löschen des Requirements:', err);
      return false;
    }
  }

  // Legt Versand-Requirements aus dem Description-Parsing an. Backend
  // blockt, wenn schon Versand-Requirements existieren — der Aufrufer
  // sollte den Button nur zeigen, wenn versandRequirements leer ist.
  async parseFromDescription(operationId: number): Promise<{ requirements: OperationRequirement[]; createdCount: number } | { error: string }> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ requirements: OperationRequirement[]; created_count: number }>(
          this.basePath + `operations/${operationId}/requirements/parse_from_description.json`,
          {},
          { withCredentials: true }
        )
      );
      return { requirements: result.requirements, createdCount: result.created_count };
    } catch (err: any) {
      console.error('Fehler beim Parsing der Auftragsbeschreibung:', err);
      return { error: err?.error?.error || 'Parsing fehlgeschlagen' };
    }
  }

  /** Verfuegbarkeitsprognose des Auftragsbedarfs. Eigener Endpoint, weil die
   *  Rechnung mehrere gruppierte Queries kostet — die Ticketseite laedt sie
   *  nach, waehrend die Bedarfsliste schon steht. */
  async loadAvailability(operationId: number): Promise<AvailabilityResult | null> {
    try {
      return await firstValueFrom(
        this.http.get<AvailabilityResult>(
          this.basePath + `operations/${operationId}/requirement_availability.json`,
          { withCredentials: true }
        )
      );
    } catch (err) {
      console.error('Fehler beim Laden der Verfügbarkeitsprognose:', err);
      return null;
    }
  }

  /** Uebernimmt die fehlenden Mengen als Versandartikel. Ohne `entries` werden
   *  alle laut Prognose fehlenden Mengen uebernommen; das Backend deckelt die
   *  Menge in jedem Fall auf die aktuelle Fehlmenge. */
  async adoptMissing(
    operationId: number,
    entries?: Array<{ article_template_id: number; quantity: number }>,
  ): Promise<{ requirements: OperationRequirement[]; availability: AvailabilityResult } | null> {
    try {
      return await firstValueFrom(
        this.http.post<{ adopted: OperationRequirement[]; requirements: OperationRequirement[]; availability: AvailabilityResult }>(
          this.basePath + `operations/${operationId}/requirements/adopt_missing.json`,
          entries ? { entries } : {},
          { withCredentials: true }
        )
      );
    } catch (err) {
      console.error('Fehler beim Übernehmen der Fehlmengen:', err);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Excel-Bulk-Edit-Workflow fuer Versand-/Staging-Artikel ueber mehrere
  // Operations: Export → Bearbeiten → Preview → Apply.
  // ---------------------------------------------------------------------------

  /** Triggert den Excel-Download via Blob-Response (Browser speichert die Datei). */
  async exportExcel(operationIds: number[], projectId: number, templateSource: ExcelTemplateSource): Promise<Blob> {
    return firstValueFrom(
      this.http.post(`${this.excelBasePath}/export`, {
        operation_ids: operationIds,
        project_id: projectId,
        template_source: templateSource,
      }, {
        responseType: 'blob',
        withCredentials: true,
      }),
    );
  }

  async importExcelPreview(file: File): Promise<ExcelPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    return firstValueFrom(
      this.http.post<ExcelPreviewResult>(`${this.excelBasePath}/import_preview`, formData, {
        withCredentials: true,
      }),
    );
  }

  async importExcelApply(changes: ExcelChange[]): Promise<ExcelApplyResult> {
    return firstValueFrom(
      this.http.post<ExcelApplyResult>(`${this.excelBasePath}/import_apply`, { changes }, {
        withCredentials: true,
      }),
    );
  }

  /** Atomarer „Zeile pro Config"-Apply: Mengen-Diffs, Versandkonfigurationen und
   *  der Mengen-Split gehen in EINEM Request raus und werden serverseitig in
   *  EINER Transaction angewandt. */
  async importExcelApplyAtomic(
    changes: ExcelChange[],
    configs: ExcelApplyConfigSpec[],
    split: ExcelApplySplitGroup[],
  ): Promise<ExcelApplyResult> {
    return firstValueFrom(
      this.http.post<ExcelApplyResult>(`${this.excelBasePath}/import_apply`, { changes, configs, split }, {
        withCredentials: true,
      }),
    );
  }
}
